/* tslint:disable:max-line-length */
import _ from 'lodash';
import i18next from 'i18next';
import { setPathLocale } from '../../common/localeShared';
import frontendStore from './frontendStore';
import { TRouter } from './router/types';

function convertFromPasteCharacterToSearch(region: string, combinedName: string) {
  const [, /* flags */ name, ...realm] = combinedName.split('-');
  return `${region}-${name}-${realm.join('-')}`;
}

function normalizeCharacterList(source: any): string[] {
  if (!source) {
    return [];
  }

  if (typeof source === 'string') {
    return [source];
  }

  if (Array.isArray(source)) {
    return source.flatMap((item: any) => normalizeCharacterList(item));
  }

  if (_.isPlainObject(source)) {
    return Object.keys(source)
      .sort((a, b) => Number(a) - Number(b))
      .flatMap((key) => normalizeCharacterList(source[key]));
  }

  return [];
}

function isValidPastedCharacter(value: string): boolean {
  const [roleMask, name, ...realmParts] = value.split('-');
  return /^\d+$/.test(roleMask) && !!name && realmParts.join('-').length > 0;
}

function getRouteFromRaiderIoUrl(value: string): string | null {
  try {
    const url = new URL(value.trim());
    const hostname = url.hostname.toLowerCase();
    if (hostname !== 'raider.io' && !hostname.endsWith('.raider.io')) {
      return null;
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

export default function init(router: TRouter) {
  const state = frontendStore.getState<any, any>();

  const mplusActivityIds = _.compact(_.flatten(state.siteMeta.dungeons.map((d: any) => d.group_finder_activity_ids)));

  // With the addon, there is a way to copy a player's Raider.IO URL from in-game,
  // so we let users paste them anywhere in the site to navigate to it.
  (window as any).addEventListener('paste', (e: any) => {
    if (!e.clipboardData) {
      return;
    }

    const pastedData = e.clipboardData.getData('text');
    if (!pastedData) {
      return;
    }

    // Check that we're NOT within an input field
    const { activeElement } = document;
    if (activeElement) {
      const node = activeElement as Element;
      const allowedTypes = ['checkbox', 'radio', 'button'];
      const isTextInput =
        node &&
        ((node.nodeName === 'INPUT' && allowedTypes.indexOf((node as HTMLInputElement).getAttribute('type') || '__valid') === -1) ||
          node.nodeName === 'TEXTAREA');

      if (isTextInput) {
        return;
      }
    }

    if (pastedData.indexOf('://') !== -1) {
      const route = getRouteFromRaiderIoUrl(pastedData);
      if (route) {
        const localizedPath = setPathLocale(route, i18next.language);
        router.navigate(localizedPath);
      }
    } else {
      /*
      {
        "group": [
          "4-Aspyrox-Skullcrusher"
        ],
        "queue": [
          "1-Alklay-Azralon",
          "1-Greanxar-Azralon",
          [
            "1-Filasa-Illidan",
            "1-Guttermage-BleedingHollow",
            "1-Deädpool-LaughingSkull"
          ],
          "1-Voodhu-Mannoroth"
        ],
        "activity": 502,
        "region": "us"
      }
    */
      try {
        const parsed = JSON.parse(pastedData);
        if (parsed.region && (parsed.group || parsed.queue)) {
          const characters = [...normalizeCharacterList(parsed.group), ...normalizeCharacterList(parsed.queue)]
            .filter(isValidPastedCharacter)
            .map(convertFromPasteCharacterToSearch.bind(null, parsed.region));

          if (characters.length === 0) {
            return;
          }

          const queryParts = characters
            .map((ch: any, index: number) => {
              return `character[${index}][eq]=${ch}`;
            })
            .join('&');

          // const raidingFilters = [
          //   'itemLevelEquipped',
          //   'raiding.uldir.normal.progress[0][gte]=0',
          //   'raiding.antorus-the-burning-throne.normal.progress[0][gte]=0',
          // ].join('&');

          const mythicPlusFilters = [
            'itemLevelEquipped',
            // 'heartOfAzerothLevel[0][gte]=0',
            `mplus.${state.siteConfig.currentSeasonSlug}.all.score[0][gte]=0`,
            `mplus.${state.siteConfig.currentSeasonSlug}.all.bestRun.level[0][gte]=0`,
            `mplus.${state.siteConfig.previousSeasonSlug}.all.score[0][gte]=0`,
            `raiding.${state.siteConfig.currentRaidSlug}.normal.progress[0][gte]=0`
            // 'mplus.season-pre-bfa.all.score[0][gte]=0',
            // 'mplus.season-pre-bfa.all.bestRun.level[0][gte]=0'
            // 'mplus.season-7.3.2.all.score[0][gte]=0',
            // 'mplus.season-7.3.2.all.bestRun.level[0][gte]=0'
          ].join('&');

          const defaultFilters = [
            'itemLevelEquipped',
            // 'heartOfAzerothLevel[0][gte]=0',
            // 'corruption.cloakRank[0][gte]=0',
            `raiding.${state.siteConfig.currentRaidSlug}.normal.progress[0][gte]=0`,
            `raiding.${state.siteConfig.previousRaidSlug}.normal.progress[0][gte]=0`,
            `mplus.${state.siteConfig.currentSeasonSlug}.all.score[0][gte]=0`
            // `mplus.${state.siteConfig.currentSeasonSlug}.all.bestRun.level[0][gte]=0`
            // 'mplus.${previousSeason}.all.score[0][gte]=0'
          ].join('&');

          let filters = defaultFilters;
          if (mplusActivityIds.indexOf(parsed.activity) !== -1) {
            filters = mythicPlusFilters;
          }

          router.navigate(`${setPathLocale('/search', i18next.language)}?type=character&${filters}&${queryParts}`);
        }
      } catch (err) {
        // did not match
      }
    }
  });
}
